import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import {
  CommunityPostTypeAnnouncement,
  CommunityPostTypeDiscussion,
  CommunityPostTypeIntroduction,
  CommunityPostTypeMilestone,
  CommunityPostTypeQuestion,
} from '@onesource/icons';
import { POST_TYPE, POST_TYPE_DEFINITIONS, type TPostType } from '@onesource/schemas';
import { useMemo, useState, type ComponentType, type FC, type MouseEvent, type SVGProps } from 'react';

import { FLUID_TYPE } from './fluid-type';
import { FormSelectTrigger } from './form-select-trigger';

/**
 * Post-type glyphs from the icons package. Each SVG carries its own fill baked
 * to match {@link POST_TYPE_CHIP_COLORS} (e.g. coral for Announcement), so it
 * sits directly on the soft tinted tile without extra coloring.
 */
export const POST_TYPE_ICONS: Record<TPostType, ComponentType<SVGProps<SVGSVGElement>>> = {
  [POST_TYPE.ANNOUNCEMENT]: CommunityPostTypeAnnouncement,
  [POST_TYPE.QUESTION]: CommunityPostTypeQuestion,
  [POST_TYPE.INTRODUCTION]: CommunityPostTypeIntroduction,
  [POST_TYPE.MILESTONE]: CommunityPostTypeMilestone,
  [POST_TYPE.DISCUSSION]: CommunityPostTypeDiscussion,
};

interface PostTypeIconProps {
  type: TPostType;
  /** Rendered square size in px. */
  size?: number;
}

/**
 * Renders the glyph for a post type, optically centered in a square `size`×
 * `size` footprint. The source SVGs have inconsistent viewBoxes (15×16, 15×13,
 * 15×15), so we center each glyph in a flex box and let it scale to fit rather
 * than trusting each container's own alignment.
 */
export const PostTypeIcon: FC<PostTypeIconProps> = ({ type, size = 16 }) => {
  const Icon = POST_TYPE_ICONS[type];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
        lineHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Pin the SVG to explicit px so it never falls back to its `1em` default
          (which, inside a MUI Chip icon slot, resolves to the chip's font-size
          and overflows the badge). */}
      <Icon width={size} height={size} style={{ display: 'block', flexShrink: 0 }} />
    </Box>
  );
};

/**
 * Background / text colors per post type. Single source of truth for the post
 * card badges and the composer's post type select. Each type pairs a soft
 * tinted background with a saturated text/icon color of the same hue (e.g.
 * mint tint + teal text for Announcement).
 */
export const POST_TYPE_CHIP_COLORS: Record<TPostType, { bg: string; color: string }> = {
  // Matches the RYC Community post-type taxonomy: coral / green / blue / pink / purple.
  [POST_TYPE.ANNOUNCEMENT]: { bg: '#FFEAEE', color: '#FF7262' },
  [POST_TYPE.QUESTION]: { bg: '#F0FFFB', color: '#46D7AE' },
  [POST_TYPE.INTRODUCTION]: { bg: '#F4FBFF', color: '#88C8ED' },
  [POST_TYPE.MILESTONE]: { bg: '#F8E9E8', color: '#E893C7' },
  [POST_TYPE.DISCUSSION]: { bg: '#F5F2FF', color: '#B1A2EA' },
};

type TPostTypeDefinition = (typeof POST_TYPE_DEFINITIONS)[number];

/** Menu paper styling shared by the composer select and the feed filter. */
export const POST_TYPE_MENU_PAPER_SX = {
  borderRadius: '12px',
  mt: 0.5,
  minWidth: 300,
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
} as const;

/** Alphabetical menu order, shared by the composer select and the feed filter. */
export const sortPostTypeOptions = (options: readonly TPostTypeDefinition[]): TPostTypeDefinition[] =>
  [...options].sort((a, b) => a.label.localeCompare(b.label));

/**
 * One post type menu row: colored emoji tile, label + one-line description,
 * check mark + tinted background when selected. A render function (not a
 * component) so the MenuItem stays a direct Menu child and keyboard focus
 * management keeps working.
 */
export const renderPostTypeMenuItem = (def: TPostTypeDefinition, selected: boolean, onClick: () => void) => {
  const colors = POST_TYPE_CHIP_COLORS[def.value];
  return (
    <MenuItem
      key={def.value}
      selected={selected}
      onClick={onClick}
      sx={{
        borderRadius: '10px',
        mx: 0.75,
        my: 0.25,
        px: 1,
        py: 0.75,
        gap: 1.25,
        alignItems: 'center',
        '&.Mui-selected': { bgcolor: colors.bg, '&:hover': { bgcolor: colors.bg } },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '10px',
          bgcolor: colors.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <PostTypeIcon type={def.value} size={18} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{ fontFamily: 'Figtree, sans-serif', fontSize: FLUID_TYPE.labelSm, fontWeight: 600, color: '#333' }}
        >
          {def.label}
        </Typography>
        {/* Supporting hint only — quieter than the label, never competing. */}
        <Typography noWrap sx={{ fontFamily: 'Figtree, sans-serif', fontSize: FLUID_TYPE.overline, color: '#a8a8a8' }}>
          {def.description}
        </Typography>
      </Box>
      {selected && <CheckIcon fontSize="small" sx={{ color: colors.color, flexShrink: 0 }} />}
    </MenuItem>
  );
};

interface PostTypeSelectProps {
  value?: TPostType | null;
  onChange: (value: TPostType) => void;
  /** Selectable definitions — already filtered for admin gating by the caller. */
  options: readonly TPostTypeDefinition[];
}

/**
 * Post type picker for the composer / edit form. The trigger button is
 * neutral; the menu lists each type with its icon in a colored tile, label
 * and a one-line description.
 */
export const PostTypeSelect: FC<PostTypeSelectProps> = ({ value, onChange, options }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Alphabetical order in the menu.
  const selectableOptions = useMemo(() => sortPostTypeOptions(options), [options]);

  const selectedDef = POST_TYPE_DEFINITIONS.find(o => o.value === value);

  const handleSelect = (postType: TPostType) => {
    onChange(postType);
    setAnchorEl(null);
  };

  return (
    <>
      <FormSelectTrigger
        startIcon={selectedDef ? <PostTypeIcon type={selectedDef.value} size={15} /> : undefined}
        onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
      >
        {selectedDef ? selectedDef.label : 'Post type'}
      </FormSelectTrigger>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: POST_TYPE_MENU_PAPER_SX } }}
      >
        {selectableOptions.map(def => renderPostTypeMenuItem(def, def.value === value, () => handleSelect(def.value)))}
      </Menu>
    </>
  );
};
